import { useState } from 'react';
import { Alert, Button, Card, Input, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  blockAccount,
  cancelScheduledStatus,
  listBlockings,
  unblockAccount,
  type PAAccountBlockings,
  type PABlock,
  type PABlockAction,
  type PABlockings,
  type PACapabilities,
  type PAScheduledStatus,
} from '../api/access';
import { useCapabilities } from '../access/useCapabilities';
import { formatCivilDate, formatInstant, NO_VALUE } from '../format';
import { DataTable, type Column } from '../money/DataTable';
import { MoneyPage, QueryFrame } from '../money/MoneyPage';
import { usePAQuery } from '../money/usePAQuery';
import { accountStatusLabel, accountStatusTone, scheduledTargetLabel } from '../money/labels';
import { errorText } from '../session/errorText';

/**
 * ============================================================================
 * «БЛОКИРОВКИ» — free-form voluntary blocking, the «отпуск» (§13A, F19 / F21)
 * ============================================================================
 *
 * ⚠ ONLY MECHANISM A EXISTS. §13B's TEMPLATE-based blocking is POST-LAUNCH and the reason
 * is measured rather than argued: the template entity, its duration range, its permitted
 * start-date window and its per-period allowance counter appear NOWHERE in the Go tree or
 * in any migration. So this screen offers **a start date and nothing else** — no duration,
 * no «до какого числа», no allowance counter. Ending a block early is «Разблокировать»,
 * and an `until` would create the paired auto-activation row whose cancellation is the trap
 * the cancel route refuses (see below).
 *
 * # The four server verdicts this screen obeys and never re-derives
 *
 *  1. **`can_block` / `can_unblock`.** They come from the same predicate the write routes
 *     apply — blocking only from ACTIVE, unblocking only from a USER block — because
 *     Appendix L §3 reserves financial suspension, administrative blocks and the disabled
 *     state to staff. The tempting client copy is `status === 0`, and it is a second
 *     statement of a server rule that can drift from the one that enforces: the server may
 *     refuse for a reason this screen has never heard of, and then the button is a `409`
 *     the subscriber cannot act on.
 *  2. **`earliest_block_date` / `earliest_unblock_date`.** Civil dates in the OPERATOR's
 *     billing time zone, which the browser cannot compute — it does not know
 *     `general.billing_timezone`, and around every local midnight a browser-derived
 *     «today» is simply a different day. They are the date field's `min` AND its prefill,
 *     and the two forms read DIFFERENT fields because an operator may permit a same-day
 *     unblock while forbidding a same-day block.
 *  3. **`applied`.** The server answers 200 for «took effect now» and 201 for «scheduled»,
 *     and this field carries the same fact in the body. The two outcomes get two
 *     sentences: a subscriber who is told «приостановлено» when it was merely booked for
 *     next week will telephone about a service that is still running.
 *  4. **`can_cancel`.** ⚠ THE SERVER'S OWN VERDICT ON «Отменить», per plan row, from the same
 *     predicate `DELETE /pa/accounts/{id}/schedule/{sid}` enforces. ~~`parent_scheduled_id`
 *     is what decides whether the button is rendered~~ *(struck 2026-08-12: it decided that
 *     until the server published its verdict, and the derivation was too narrow — see below.)*
 *     This list carries the OPERATOR's plans as well as the subscriber's, and an operator's
 *     plan has NO parent; keying the button on «no parent» therefore offered «Отменить» on a
 *     staff-created administrative block, which the server now refuses by name
 *     (`pa.schedule_not_cancellable`) — and before it did, took with a 200.
 *  5. **`parent_scheduled_id`.** ⚠ NON-NULL MEANS THE ROW IS THE **END DATE** OF THE BLOCK
 *     IT NAMES, not a plan of its own — a timed block created by staff is TWO rows.
 *     Cancellation cascades PARENT → child and never child → parent, so cancelling the end
 *     date silently converts a two-week «отпуск» into an INDEFINITE block. The server refuses
 *     it (`pa.cancel_parent_first`) and `can_cancel` is false for it, so no button is
 *     rendered — and this field is what still chooses WHICH «нельзя» sentence appears, because
 *     «это дата окончания» and «это план оператора» are different facts and only the second
 *     is one the subscriber can do anything about.
 *
 * # What is deliberately NOT on this screen
 *
 *   - **a «Тарифный план» column**, which §13A-1 draws. ~~Tariff CHANGE is not built, so the
 *     word would promise a section that does not exist~~ *(struck 2026-08-12, W4 P3: the
 *     tariff section IS built — `TariffScreen` — so the original reason is gone. The column
 *     still is not drawn, for the reason that outlives it: the tariff is on the dashboard's
 *     account card AND in its own section, and a third statement of the same fact on a screen
 *     about blocking is a place for the three to disagree.)*
 *   - **a COMMENT field.** Neither F19 nor F21 offers one, and the server writes a fixed
 *     Russian sentence — a box whose text landed on an operator's screen as
 *     subscriber-authored prose is not something either spec asked for.
 *   - **a FIGURE for what a block costs.** Appendix H §3.15 is the engine's: the
 *     cost-at-blocking price where the tariff configures one, and NOTHING where it does
 *     not. The portal cannot know which, so it says exactly that and quotes no number —
 *     Appendix L §3 says the portal does not indicate it at all, and one honest sentence
 *     is better than silence and better than an invented figure.
 */
export function BlockingsScreen() {
  return (
    <MoneyPage titleKey="blockings.title" leadKey="blockings.lead">
      {(contract) => <BlockingsBody contractID={contract.id} />}
    </MoneyPage>
  );
}

type Form = { account: PAAccountBlockings; action: 'block' | 'unblock' } | null;

function BlockingsBody({ contractID }: { contractID: number }) {
  const { t } = useTranslation();
  /*
    ⚠ THE CAPABILITY READ GATES THE WHOLE BODY HERE, unlike on the dashboard.

    This screen's entire purpose is the two dated forms, and their earliest permitted date
    is the SERVER's. A screen that rendered the buttons with the capability read still in
    flight would have to invent a `min` from the browser clock — which is the one thing the
    field exists to prevent — and a screen that rendered them after a FAILED read would be
    guessing. So a failure is the error frame with «Повторить», which is honest, rather than
    «нельзя», which would tell him his operator had switched something off.
  */
  const caps = useCapabilities();
  const { query, reload } = usePAQuery<PABlockings>(() => listBlockings(contractID), [contractID]);

  const [form, setForm] = useState<Form>(null);
  const [dateText, setDateText] = useState('');
  const [cancelling, setCancelling] = useState<{ accountID: number; plan: PAScheduledStatus } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState('');
  const [localError, setLocalError] = useState('');

  return (
    <QueryFrame query={caps.query} reload={caps.reload}>
      {(capabilities) => (
        <QueryFrame query={query} reload={reload}>
          {(data) => {
            const openForm = (account: PAAccountBlockings, action: 'block' | 'unblock') => {
              setError(null);
              setNotice('');
              setLocalError('');
              // THE SERVER'S OWN EARLIEST DATE, per form. Never `new Date()`.
              setDateText(
                action === 'block'
                  ? capabilities.earliest_block_date
                  : capabilities.earliest_unblock_date,
              );
              setForm({ account, action });
            };

            const apply = async () => {
              if (!form) return;
              if (dateText.trim() === '') {
                setLocalError(t('blockings.dateRequired'));
                return;
              }
              setBusy(true);
              setError(null);
              setLocalError('');
              try {
                const act =
                  form.action === 'block'
                    ? await blockAccount(contractID, form.account.account_id, dateText)
                    : await unblockAccount(contractID, form.account.account_id, dateText);
                setNotice(outcomeSentence(form.action, act, t));
                setForm(null);
                // The list now carries the new status or the new pending row, and only the
                // server knows which — so it is re-read rather than patched here.
                reload();
              } catch (err) {
                setError(err);
                // A refusal means the verdict that produced the button is stale (staff
                // changed the status while the form was open, or the day rolled over).
                setForm(null);
                reload();
              } finally {
                setBusy(false);
              }
            };

            const cancelPlan = async () => {
              if (!cancelling) return;
              setBusy(true);
              setError(null);
              try {
                await cancelScheduledStatus(contractID, cancelling.accountID, cancelling.plan.id);
                setNotice(t('blockings.planCancelled'));
              } catch (err) {
                setError(err);
              } finally {
                setBusy(false);
                setCancelling(null);
                reload();
              }
            };

            return (
              <>
                {notice ? (
                  <Alert type="success" showIcon message={notice} style={{ marginBottom: 16 }} />
                ) : null}
                {error ? (
                  <Alert
                    type="error"
                    showIcon
                    message={errorText(error, t)}
                    style={{ marginBottom: 16 }}
                  />
                ) : null}

                {data.items.length === 0 ? (
                  <Alert type="info" showIcon message={t('blockings.noAccounts')} />
                ) : null}

                {data.items.map((account) => (
                  <AccountBlock
                    key={account.account_id}
                    account={account}
                    onBlock={() => openForm(account, 'block')}
                    onUnblock={() => openForm(account, 'unblock')}
                    onCancelPlan={(plan) =>
                      setCancelling({ accountID: account.account_id, plan })
                    }
                  />
                ))}

                <Modal
                  open={form !== null}
                  title={t(form?.action === 'unblock' ? 'blockings.unblockTitle' : 'blockings.blockTitle')}
                  okText={t('blockings.apply')}
                  cancelText={t('blockings.cancel')}
                  okButtonProps={{ loading: busy }}
                  onOk={() => void apply()}
                  onCancel={() => {
                    setForm(null);
                    setLocalError('');
                  }}
                >
                  {form ? (
                    <DateForm
                      action={form.action}
                      login={form.account.login}
                      capabilities={capabilities}
                      dateText={dateText}
                      localError={localError}
                      onDateText={(next) => {
                        setLocalError('');
                        setDateText(next);
                      }}
                    />
                  ) : null}
                </Modal>

                <Modal
                  open={cancelling !== null}
                  title={t('blockings.cancelPlanTitle')}
                  okText={t('blockings.cancelPlanConfirm')}
                  cancelText={t('blockings.cancelPlanBack')}
                  okButtonProps={{ danger: true, loading: busy }}
                  onOk={() => void cancelPlan()}
                  onCancel={() => setCancelling(null)}
                >
                  <p>{t('blockings.cancelPlanText')}</p>
                </Modal>
              </>
            );
          }}
        </QueryFrame>
      )}
    </QueryFrame>
  );
}

/**
 * outcomeSentence chooses between the four wordings — block/unblock × now/scheduled — from
 * the SERVER'S OWN ANSWER and from nothing the subscriber typed.
 *
 * ⚠ IT READS `act.applied` AND `act.scheduled.run_date`, AND THE SECOND OF THOSE IS A
 * CORRECTION WORTH RECORDING RATHER THAN QUIETLY MAKING. Until 2026-08-12 this function took
 * the `dateText` the form had sent, because `paScheduledWire` published only `run_at` — a
 * `time.Time` the READ path passed through untouched — so rendering it as a day was a zone
 * guess, one day out for any browser west of a +03 operator. **That backend defect is fixed**
 * (W4 B3 projects the instant into the billing location and publishes the civil day beside
 * it), so the workaround came out with it: a workaround left standing beside a fixed server is
 * a SECOND SOURCE OF TRUTH about the same fact, and the two only have to disagree once.
 *
 * ⚠ AND THE ECHO IS THE AUTHORITATIVE STATEMENT, not merely an equivalent one. What the
 * subscriber typed is what he ASKED FOR; `run_date` is the day the row was WRITTEN with,
 * resolved in the operator's calendar by the code that stores it. They coincide in today's
 * server (`resolveEffectiveDate` schedules at 00:00 of exactly the day it parsed) and the
 * screen must not depend on their coinciding — the neighbouring tariff surface already snaps a
 * requested day forward to a cycle boundary, and the day that arrangement reaches this route
 * the input is simply the wrong thing to print.
 *
 * A scheduled outcome with no `scheduled` row is a body this server does not send; it is
 * rendered as «—» rather than as a guessed day, because a sentence naming a date nobody
 * supplied is the failure this whole comment is about.
 */
function outcomeSentence(
  action: 'block' | 'unblock',
  act: PABlockAction,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  if (act.applied) {
    return t(action === 'block' ? 'blockings.blockedNow' : 'blockings.unblockedNow');
  }
  return t(action === 'block' ? 'blockings.blockScheduled' : 'blockings.unblockScheduled', {
    date: act.scheduled ? formatCivilDate(act.scheduled.run_date) : NO_VALUE,
  });
}

/** The form inside the modal: an account, a date, and the operator's own rules about it. */
function DateForm({
  action,
  login,
  capabilities,
  dateText,
  localError,
  onDateText,
}: {
  action: 'block' | 'unblock';
  login: string;
  capabilities: PACapabilities;
  dateText: string;
  localError: string;
  onDateText: (next: string) => void;
}) {
  const { t } = useTranslation();
  const blocking = action === 'block';
  const earliest = blocking ? capabilities.earliest_block_date : capabilities.earliest_unblock_date;
  const sameDayAllowed = blocking
    ? capabilities.block_same_day_allowed
    : capabilities.unblock_same_day_allowed;

  return (
    <>
      <span className="pa-field-label">{t('blockings.accountLabel')}</span>
      <p className="pa-contact-value">
        <span className="mono">{login}</span>
      </p>

      <label className="pa-field-label" htmlFor="pa-block-date">
        {t('blockings.dateLabel')}
      </label>
      <Input
        id="pa-block-date"
        type="date"
        size="large"
        value={dateText}
        // ⚠ BOTH THE FLOOR AND THE PREFILL ARE THE SERVER'S FIGURE. A `min` derived from
        // the browser clock is wrong for every subscriber whose device is not in the
        // operator's zone, and wrong for everybody for the hours around local midnight.
        min={earliest}
        status={localError ? 'error' : undefined}
        onChange={(e) => onDateText(e.target.value)}
      />
      {localError ? <p className="pa-field-error">{localError}</p> : null}
      <p className="pa-field-hint">
        {t(blocking ? 'blockings.dateHintBlock' : 'blockings.dateHintUnblock')}
      </p>
      {/* Said only when the operator FORBIDS a same-day start: otherwise it is a rule that
          does not apply here, and a form that recites inapplicable rules trains the eye to
          skip the one that matters. */}
      {!sameDayAllowed ? (
        <Alert
          type="info"
          showIcon
          message={t(
            blocking ? 'blockings.sameDayBlocked' : 'blockings.sameDayUnblockBlocked',
            { date: formatCivilDate(earliest) },
          )}
          style={{ marginTop: 12 }}
        />
      ) : null}
      {/* §3.15, once, with no figure — see this file's header. */}
      {blocking ? <p className="pa-field-hint">{t('blockings.chargeNote')}</p> : null}
    </>
  );
}

/** One Account: its state, the actions the SERVER admits, its pending plans, its history. */
function AccountBlock({
  account,
  onBlock,
  onUnblock,
  onCancelPlan,
}: {
  account: PAAccountBlockings;
  onBlock: () => void;
  onUnblock: () => void;
  onCancelPlan: (plan: PAScheduledStatus) => void;
}) {
  const { t } = useTranslation();
  const tone = accountStatusTone(account.status, false);

  const columns: ReadonlyArray<Column<PABlock>> = [
    {
      key: 'what',
      labelKey: 'blockings.columnWhat',
      // The status that WAS set, worded as a state — this is history, so «Приостановлена по
      // вашей заявке» is what happened rather than what is about to.
      render: (b) => accountStatusLabel(b.status, false, t),
    },
    {
      key: 'period',
      labelKey: 'blockings.columnPeriod',
      track: 'minmax(0, 2fr)',
      render: (b) =>
        b.removed_at
          ? t('blockings.historyPeriod', {
              from: formatInstant(b.set_at),
              to: formatInstant(b.removed_at),
            })
          : t('blockings.historyOpen', { from: formatInstant(b.set_at) }),
    },
    {
      key: 'who',
      labelKey: 'blockings.columnWho',
      render: (b) => t(b.is_self ? 'blockings.bySelf' : 'blockings.byStaff'),
    },
  ];

  return (
    <Card
      className="pa-block"
      title={<span className="mono">{account.login}</span>}
      extra={
        <span className={`pa-status pa-status-${tone}`}>
          {accountStatusLabel(account.status, false, t)}
        </span>
      }
      data-testid={`pa-blockings-${account.account_id}`}
    >
      <div className="pa-actions">
        {account.can_block ? (
          <Button type="primary" size="large" block onClick={onBlock}>
            {t('blockings.block')}
          </Button>
        ) : null}
        {account.can_unblock ? (
          <Button type="primary" size="large" block onClick={onUnblock}>
            {t('blockings.unblock')}
          </Button>
        ) : null}
        {/* Neither verdict admits an action: say which kind of «no» this is. The two
            sentences are different facts — a state only staff can change, and a state that
            is not blockable at all. */}
        {!account.can_block && !account.can_unblock ? (
          <Alert
            type="info"
            showIcon
            message={t(account.status === 10 ? 'blockings.disabledAccount' : 'blockings.staffOnly')}
          />
        ) : null}
      </div>

      {account.scheduled.length > 0 ? (
        <div className="pa-subblock">
          <h3 className="pa-section-subtitle">{t('blockings.scheduledTitle')}</h3>
          {account.scheduled.map((plan) => (
            <div className="pa-plan" key={plan.id}>
              <span className="pa-plan-what">
                {scheduledTargetLabel(plan.target_status, t)}
                {' — '}
                {/* ⚠ THE SERVER'S CIVIL DAY, not `run_at` in the browser's zone. Until
                    2026-08-12 this printed `formatInstantDay(plan.run_at)` and the instant
                    travelled unprojected, so a plan for the 10th read as the 9th for any
                    browser west of a +03 operator — the zone guess this file's header recorded
                    as known. `run_date` is the field that exists to remove it. */}
                {t('blockings.scheduledAt', { when: formatCivilDate(plan.run_date) })}
              </span>
              {/* ⚠ THE ONE CONTROL THIS SCREEN WITHHOLDS, AND IT IS WITHHELD BY THE SERVER'S
                  OWN VERDICT. `can_cancel` comes from the same predicate the DELETE enforces,
                  so the button exists exactly where it can succeed. It was derived here from
                  `plan.parent_scheduled_id === null` until 2026-08-12, which covered the
                  auto-activation child and MISSED the operator's own plans — this list carries
                  those too, and offering «Отменить» on one is a control whose only possible
                  answer is `pa.schedule_not_cancellable`.

                  `parent_scheduled_id` still chooses WHICH refusal sentence to show, because
                  the two are different facts: an end date cannot be cancelled separately at
                  all (rule 4 — cancelling it converts a timed «отпуск» into an indefinite
                  one), while an operator's plan can be lifted, just not from here. */}
              {plan.can_cancel ? (
                <Button size="large" onClick={() => onCancelPlan(plan)}>
                  {t('blockings.cancelPlan')}
                </Button>
              ) : (
                <p className="pa-field-hint">
                  {t(plan.parent_scheduled_id === null ? 'blockings.staffPlanNote' : 'blockings.childNote')}
                </p>
              )}
            </div>
          ))}
        </div>
      ) : null}

      <div className="pa-subblock">
        <h3 className="pa-section-subtitle">{t('blockings.historyTitle')}</h3>
        <DataTable
          columns={columns}
          rows={account.history}
          rowKey={(b) => b.id}
          emptyKey="blockings.historyEmpty"
        />
      </div>
    </Card>
  );
}
